import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';

const TimeIntegrityIndicator = ({ refreshKey }) => {
  const { theme } = useTheme();
  const c = colors[theme];
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getTimeIntegrity();
      setData(res.data);
    } catch (e) {
      console.error('Error loading time integrity:', e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (refreshKey > 0) {
      api.getTimeIntegrity().then((res) => setData(res.data)).catch(() => {});
    }
  }, [refreshKey]);

  if (!data) return null;

  const { time_integrity_score, contract_compliance, severity_summary = {} } = data;
  const blockers = severity_summary.blockers || 0;
  const compliant = contract_compliance?.compliant || 0;
  const total = contract_compliance?.total_engines || 4;

  const scoreColor = time_integrity_score >= 80 ? '#16a34a' : time_integrity_score >= 50 ? '#d97706' : '#dc2626';
  const statusColor = blockers > 0 ? '#dc2626' : compliant === total ? '#16a34a' : '#d97706';

  return (
    <motion.button
      onClick={() => navigate('/configuration?tab=time-integrity')}
      className="rounded-xl p-4 mb-4 w-full text-left transition-all hover:opacity-90"
      style={{ background: c.cardBg, boxShadow: getNeumorphicShadow(theme) }}
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="time-integrity-indicator"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: statusColor + '12' }}>
            <Clock size={16} style={{ color: statusColor }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold" style={{ color: c.textPrimary }}>Time Integrity</span>
              <span className="text-lg font-bold" style={{ color: scoreColor }}>{time_integrity_score}%</span>
            </div>
            <div className="flex items-center gap-3 mt-0.5">
              <span className="text-xs" style={{ color: c.textSecondary }}>
                {compliant}/{total} engines compliant
              </span>
              {blockers > 0 && (
                <span className="flex items-center gap-1 text-xs font-medium" style={{ color: '#dc2626' }}>
                  <AlertTriangle size={11} /> {blockers} {blockers === 1 ? 'blocker' : 'blockers'}
                </span>
              )}
            </div>
          </div>
        </div>
        <ChevronRight size={16} style={{ color: c.textSecondary }} />
      </div>
    </motion.button>
  );
};

export default TimeIntegrityIndicator;
